import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "../src/utils/css/theme.css";
import axios from "axios";
import "./utils/firebase";

import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { persistor, store } from "./redux/store";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { PostHogErrorBoundary, PostHogProvider } from "@posthog/react";

if (typeof window !== "undefined") {
  // Root cause fix: posthog-js bundles rrweb for session recording, which installs
  // a ResizeObserver that mutates the DOM synchronously inside its callback (e.g.
  // when the react-18-image-lightbox overlay toggles body overflow on mount/unmount).
  // Those mutations queue more observations in the same frame, causing Chrome to emit
  // "ResizeObserver loop completed with undelivered notifications."
  // Wrapping every observer callback in rAF defers DOM writes to the next frame,
  // breaking the same-frame loop. Must be patched before posthog.init() so every
  // observer posthog/rrweb creates goes through the shim.
  // See: https://bugs.chromium.org/p/chromium/issues/detail?id=809574
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        if (typeof requestAnimationFrame !== "undefined") {
          requestAnimationFrame(() => callback(entries, observer));
        } else {
          callback(entries, observer);
        }
      });
    }
  };

  // Belt-and-suspenders: CRA's error overlay uses a bubble-phase addEventListener,
  // so window.onerror alone cannot stop it. A capture-phase listener runs first and
  // can call stopImmediatePropagation() to prevent the bubble phase entirely.
  // This catches any observer that fires outside our patched class (e.g. native code).
  let resizeObserverLoopWarned = false;
  window.addEventListener(
    "error",
    (event) => {
      if (
        event.message === "ResizeObserver loop completed with undelivered notifications." ||
        event.message === "ResizeObserver loop limit exceeded"
      ) {
        event.stopImmediatePropagation();
        event.preventDefault();
        if (!resizeObserverLoopWarned) {
          resizeObserverLoopWarned = true;
          console.warn("[ResizeObserver] Suppressed benign loop notification (posthog/rrweb).");
        }
      }
    },
    true // capture phase — runs before CRA overlay's bubble-phase handler
  );
}

axios.defaults.withCredentials = true;
const rootElement = document.getElementById("root");
const root = rootElement ? ReactDOM.createRoot(rootElement) : null;

const renderApp = (posthogClient) => {
  if (!root) {
    throw new Error("Failed to locate #root element for React.");
  }

  const application = (
    <Provider store={store}>
      <React.StrictMode>
        <PersistGate loading={null} persistor={persistor}>
          <App />
        </PersistGate>
      </React.StrictMode>
    </Provider>
  );

  console.log("Index.js");

  if (posthogClient) {
    root.render(
      <PostHogProvider client={posthogClient}>
        <PostHogErrorBoundary>{application}</PostHogErrorBoundary>
      </PostHogProvider>
    );
    return;
  }

  root.render(application);
};

async function bootstrap() {
  const { default: posthog } = await import("posthog-js");

  posthog.init(process.env.REACT_APP_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
    defaults: "2026-01-30",
    disable_session_recording: process.env.REACT_APP_SESSION_RECORDING_ENABLED === "false",
  });

  renderApp(posthog);
}

const shouldLoadPosthog = process.env.REACT_APP_ENABLE_POSTHOG === "true";

if (shouldLoadPosthog) {
  bootstrap().catch((error) => {
    console.error("Failed to initialize PostHog. Rendering without analytics.", error);
    renderApp();
  });
} else {
  console.info("PostHog disabled via REACT_APP_ENABLE_POSTHOG. Rendering without analytics.");
  renderApp();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
